import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Tab, Tabs } from '@mui/material';
import { Check, Close, EditOutlined } from '@mui/icons-material';
import { Formik, FormikProps, useFormikContext } from 'formik';
import { useAppTranslation } from '../../services/i18n';
import { ReadOnlyField } from '../ReadOnlyField';
import { TextFormField } from '../form/TextFormField';
import { SelectFormField } from '../form/SelectFormField';
import { OptionValue } from '../../model/form';
import { apiDateToYmd, todayYmd } from '../../helpers/date';
import { WorkplaceErrorField } from '../../helpers/workplaceErrors';
import {
  financialApproversOf,
  descendantIdsOf,
  leadershipOf,
  pathOf,
  WorkplaceLeadership,
  WorkplaceNode,
  WorkplaceTree,
} from '../../helpers/workplaceTree';
import { WorkplaceAssignmentKind } from '../../store/workplaces';
import { WorkplaceAssignmentCard } from './WorkplaceAssignmentCard';
import { MembershipSummary, WorkplaceMembership } from './WorkplaceMembership';

/** the root option cannot carry null -- OptionValue values are scalars */
export const ROOT_PARENT = 0;

type DetailTab = 'detail' | 'membership';

const tabProps = (tab: DetailTab) => ({
  id: `workplace-detail-tab-${tab}`,
  'aria-controls': `workplace-detail-panel-${tab}`,
});

const panelProps = (tab: DetailTab, active: DetailTab) => ({
  role: 'tabpanel',
  id: `workplace-detail-panel-${tab}`,
  'aria-labelledby': `workplace-detail-tab-${tab}`,
  hidden: tab !== active,
});

export interface AssignmentValues {
  /** user_in_company_space ids; an empty list means the post is vacant */
  leaders: number[];
  /** user_in_company_space id; undefined means nobody stands in */
  deputy?: number;
  /** the deputy's validity -- a leader holds the post until removed */
  validFrom: string;
  validTo: string;
}

export interface WorkplaceValues {
  name: string;
  code: string;
  parentId: number;
  assignments: Record<WorkplaceAssignmentKind, AssignmentValues>;
}

interface Props {
  tree: WorkplaceTree;
  node: WorkplaceNode | null;
  /** set while a workplace that does not exist yet is being filled in */
  draftParentId?: number | null;
  /** off hides the parent and the path: a flat list has neither */
  isTreeEnabled: boolean;
  isMultipleWorkplacesEnabled: boolean;
  isCreating: boolean;
  isEditing: boolean;
  isSaving: boolean;
  /** the field the server refused on the last save */
  fieldError?: WorkplaceErrorField;
  /** hands the live form out, so leaving it can be weighed against what it holds */
  innerRef?: React.Ref<FormikProps<WorkplaceValues>>;
  onEdit: () => void;
  onCancel: () => void;
  /** one level up: out of the form first, out of the selection next */
  onEscape: () => void;
  onSave: (values: WorkplaceValues) => void;
  onLoadMembershipSummary: (id: number) => Promise<MembershipSummary | undefined>;
  /** leaves the editor for another screen, asking first if the form holds changes */
  onNavigate: (href: string) => void;
}

/**
 * Formik validates on change, blur and submit -- never because a prop arrived. The
 * server's verdict comes back well after the submit that caused it, so it has to ask
 * for a fresh run itself, otherwise the complaint would surface only on the next
 * keystroke.
 */
const RevalidateOn: React.FC<{ token: unknown }> = ({ token }) => {
  const { validateForm } = useFormikContext();
  useEffect(() => {
    validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  return null;
};

const assignmentValues = (assignments: WorkplaceLeadership): AssignmentValues => ({
  leaders: assignments.leaders.map((item) => item.user_in_company_space_id),
  deputy: assignments.deputy?.user_in_company_space_id,
  validFrom: assignments.deputy ? apiDateToYmd(assignments.deputy.valid_from) : todayYmd(),
  validTo: apiDateToYmd(assignments.deputy?.valid_to),
});

/** nobody holds anything yet on a workplace that is still being filled in */
const noAssignments: WorkplaceLeadership = { leaders: [] };

export const WorkplaceDetail: React.FC<Props> = ({
  tree,
  node,
  draftParentId,
  isTreeEnabled,
  isMultipleWorkplacesEnabled,
  isCreating,
  isEditing,
  isSaving,
  fieldError,
  innerRef,
  onEdit,
  onCancel,
  onEscape,
  onSave,
  onLoadMembershipSummary,
  onNavigate,
}) => {
  const t = useAppTranslation();
  /** the values the server last refused, so the complaint can be tied to them */
  const refused = useRef<WorkplaceValues | undefined>(undefined);
  /** survives walking the tree, so membership can be compared workplace by workplace */
  const [tab, setTab] = useState<DetailTab>('detail');

  // the fields live on Detail, so opening the form or a refused save brings that tab forward
  useEffect(() => {
    if (isCreating || isEditing || fieldError) {
      setTab('detail');
    }
  }, [isCreating, isEditing, fieldError]);

  const handleSubmit = (values: WorkplaceValues) => {
    refused.current = values;
    onSave(values);
  };

  /**
   * The records behind the form values. The picker holds user_in_company_space ids, which
   * no endpoint resolves into a person on its own, so the people already in post have to
   * be named from here -- in the picker options as well as in read mode.
   */
  const held = useMemo(
    () => ({
      leadership: node && !isCreating ? leadershipOf(node.unit) : noAssignments,
      budget: node && !isCreating ? financialApproversOf(node.unit) : noAssignments,
    }),
    [node, isCreating]
  );

  const initialValues: WorkplaceValues = useMemo(
    () => ({
      name: isCreating ? '' : node?.unit.name ?? '',
      code: isCreating ? '' : node?.unit.code ?? '',
      parentId: (isCreating ? draftParentId : node?.parentId) ?? ROOT_PARENT,
      assignments: {
        leadership: assignmentValues(held.leadership),
        budget: assignmentValues(held.budget),
      },
    }),
    [node, isCreating, draftParentId, held]
  );

  // a unit cannot be moved under itself or under one of its own descendants
  const parentOptions = useMemo(() => {
    const options: OptionValue[] = [
      { value: ROOT_PARENT, label: t('workplaces.detail.rootParent') },
    ];
    const forbidden = node && !isCreating ? new Set([node.id, ...descendantIdsOf(node)]) : null;
    const walk = (nodes: WorkplaceNode[], level: number) => {
      nodes.forEach((candidate) => {
        if (forbidden?.has(candidate.id)) {
          return;
        }
        options.push({
          value: candidate.id,
          label: (candidate.unit.code ? candidate.unit.code + ' · ' : '') + candidate.unit.name,
          // the ancestors ride along so typing a parent name narrows down to its children
          tooltip: pathOf(tree, candidate.id).slice(0, -1).join(' / '),
          params: { indent: { level } as object },
        });
        walk(candidate.children, level + 1);
      });
    };
    walk(tree.roots, 0);
    return options;
  }, [tree, node, isCreating, t]);

  if (!node && !isCreating) {
    return (
      <div className={'workplace-panel workplace-panel--placeholder workplace-detail'}>
        <div className={'workplace-panel__body workplace-detail__placeholder'}>
          <div className={'workplace-empty'}>
            <h4 className={'workplace-empty__title'}>
              {t('workplaces.detail.nothingSelectedTitle')}
            </h4>
            <p className={'workplace-empty__lead'}>
              {t(
                isTreeEnabled
                  ? 'workplaces.detail.nothingSelected'
                  : 'workplaces.detail.nothingSelectedFlat'
              )}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const isForm = isCreating || isEditing;
  /** a workplace still being filled in has nobody to list, so it gets no tabs */
  const hasTabs = !!node && !isCreating;
  const title = isCreating
    ? t('workplaces.detail.createTitle')
    : isEditing
    ? t('workplaces.detail.editTitle')
    : t('workplaces.detail.title');

  const validate = (values: WorkplaceValues) => {
    const errors: Record<string, unknown> = {};
    if (!values.name.trim()) {
      errors.name = t('form.required');
    }
    // the server refused one exact value, so the error stands only while that value is
    // still in the field -- editing it makes the complaint moot
    const refusedField = fieldError === 'parent' ? 'parentId' : fieldError;
    if (refusedField && refused.current && values[refusedField] === refused.current[refusedField]) {
      errors[refusedField] = t('workplaces.detail.error.' + fieldError);
    }
    // Formik looks an error up by the same path it reads the value from, so the key has to
    // be nested the same way; a literal "assignments.leadership.validFrom" is never found
    const assignments: Record<string, Record<string, string>> = {};
    (['leadership', 'budget'] as WorkplaceAssignmentKind[]).forEach((kind) => {
      const { deputy, validFrom, validTo } = values.assignments[kind];
      if (!deputy) {
        return;
      }
      // both dates are yyyy-MM-dd, so plain string comparison is chronological
      const messages: Record<string, string> = {};
      if (!validFrom) {
        messages.validFrom = t('workplaces.assignments.validFromRequired');
      } else if (validFrom > todayYmd()) {
        messages.validFrom = t('workplaces.assignments.validFromFuture');
      }
      if (validFrom && validTo && validTo < validFrom) {
        messages.validTo = t('workplaces.assignments.validToBeforeFrom');
      }
      if (Object.keys(messages).length) {
        assignments[kind] = messages;
      }
    });
    if (Object.keys(assignments).length) {
      errors.assignments = assignments;
    }
    return errors;
  };

  const renderBody = (formProps: FormikProps<WorkplaceValues>) => (
    <form
      className={'workplace-panel workplace-detail'}
      // the date inputs carry min/max to steer their own calendars; the rules themselves are
      // checked below, and a native bubble would only be swallowed by the inert Save button
      noValidate
      onSubmit={formProps.handleSubmit}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          onEscape();
        }
      }}
    >
      {/* dark header carrying the actions, per the v2 design */}
      <div className={'workplace-panel__head workplace-panel__head--dark'}>
        <h3 className={'workplace-panel__title'}>{title}</h3>
        <span className={'workplace-panel__spacer'} />
        {isForm ? (
          <>
            <Button
              variant={'outlined'}
              color={'inherit'}
              startIcon={<Close />}
              onClick={onCancel}
              disabled={isSaving}
              type={'button'}
            >
              {t('form.cancel')}
            </Button>
            {/* Nothing to save is not an error, so the button stays put and says so by
                being inert -- the design system asks for exactly that ("disabled, když
                nesmí TEĎ a uživatel má vidět proč/kde bude"). The dark-surface palette
                that keeps it legible lives in the theme. */}
            <Button
              variant={'contained'}
              color={'primary'}
              startIcon={<Check />}
              type={'submit'}
              disabled={isSaving || !formProps.dirty || !formProps.isValid}
            >
              {t('form.save')}
            </Button>
          </>
        ) : (
          <Button
            variant={'outlined'}
            color={'inherit'}
            startIcon={<EditOutlined />}
            onClick={onEdit}
            type={'button'}
          >
            {t('form.update')}
          </Button>
        )}
      </div>

      {hasTabs && node && (
        <Tabs value={tab} onChange={(event, value: DetailTab) => setTab(value)}>
          <Tab value={'detail'} label={t('workplaces.detail.tabs.detail')} {...tabProps('detail')} />
          <Tab
            value={'membership'}
            label={t('workplaces.detail.tabs.membership', { n: node.userCount })}
            {...tabProps('membership')}
          />
        </Tabs>
      )}

      <RevalidateOn token={fieldError} />
      {/* both panels stay mounted, so switching tabs mid-edit keeps what was typed */}
      <div
        className={'workplace-panel__body workplace-detail__body'}
        {...(hasTabs ? panelProps('detail', tab) : {})}
      >
        {/* read mode shows plain values, the same way the approval detail does */}
        <div className={'workplace-detail__row'}>
          {isForm ? (
            <>
              <TextFormField name={'code'} label={t('workplaces.detail.code')} maxlength={50} />
              <TextFormField
                name={'name'}
                label={t('workplaces.detail.name')}
                required
                autoFocus={isCreating}
                maxlength={255}
              />
            </>
          ) : (
            <>
              <ReadOnlyField label={t('workplaces.detail.code')} value={node?.unit.code} underline />
              <ReadOnlyField label={t('workplaces.detail.name')} value={node?.unit.name} underline />
            </>
          )}
        </div>

        {/* a flat list has no parent and no path to show; the fields are otherwise the same */}
        {isTreeEnabled &&
          (isForm ? (
          <SelectFormField
            name={'parentId'}
            label={t('workplaces.detail.parent')}
            placeholder={t('workplaces.detail.parentPlaceholder')}
            options={parentOptions}
            showTooltip
            shrink
            renderOption={(props, option) => (
              <li
                {...props}
                key={String(option.value)}
                style={{ paddingLeft: 16 + ((option.params?.indent as any)?.level ?? 0) * 16 }}
              >
                <span>
                  {option.label}
                  {!!option.tooltip && (
                    <span className={'workplace-detail__option-path'}> {option.tooltip}</span>
                  )}
                </span>
              </li>
            )}
          />
        ) : (
          <ReadOnlyField
            label={t('workplaces.detail.parent')}
            value={
              node?.parentId != null
                ? tree.byId.get(node.parentId)?.unit.name
                : t('workplaces.detail.rootParent')
            }
            underline
          />
          ))}

        {isTreeEnabled && node && !isCreating && (
          <p className={'workplace-detail__path'}>
            <span className={'workplace-detail__muted'}>{t('workplaces.detail.path')}</span>{' '}
            {pathOf(tree, node.id).join(' / ')}
          </p>
        )}

        {/* leadership has its own endpoints, but it is part of this one form: the cards
            follow the panel in and out of edit mode and go to the server on Save */}
        <div className={'workplace-assignments'}>
          <WorkplaceAssignmentCard
            kind={'leadership'}
            title={t('workplaces.detail.leadership')}
            leaderLabel={t('workplaces.assignments.leader')}
            values={formProps.values.assignments.leadership}
            held={held.leadership}
            isEditable={isForm}
            isSaving={isSaving}
          />
          <WorkplaceAssignmentCard
            kind={'budget'}
            title={t('workplaces.detail.financialApprovers')}
            leaderLabel={t('workplaces.assignments.financialApprover')}
            values={formProps.values.assignments.budget}
            held={held.budget}
            isEditable={isForm}
            isSaving={isSaving}
          />
        </div>
      </div>

      {hasTabs && node && (
        <div
          className={'workplace-panel__body workplace-detail__body'}
          {...panelProps('membership', tab)}
        >
          <WorkplaceMembership
            node={node}
            isTreeEnabled={isTreeEnabled}
            isMultipleWorkplacesEnabled={isMultipleWorkplacesEnabled}
            isActive={tab === 'membership'}
            onLoadSummary={onLoadMembershipSummary}
            onNavigate={onNavigate}
          />
        </div>
      )}
    </form>
  );

  return (
    <Formik
      // leaving the form or moving to another workplace rebuilds it from the server state,
      // which is what throws a cancelled edit away
      key={`${node?.id ?? 'new'}-${isForm ? 'form' : 'read'}`}
      innerRef={innerRef}
      initialValues={initialValues}
      enableReinitialize={true}
      validate={validate}
      onSubmit={handleSubmit}
    >
      {renderBody}
    </Formik>
  );
};
